import { useEffect, useMemo, useState } from "react";
import { useCustom } from "@refinedev/core";

import { todayIso } from "../i18n/format";

// kp-Epic 6 (kennelplanning-migration): the React Timeline (Gantt) canvas + contact-trace overlay.
// A faithful port of the Jmix `TimelineCanvas` geometry (clamp 14..32 col width, 110px left gutter,
// ~32px row height, month+day header with weekend/today tinting, clipped bars, deterministic pastel
// per dier via dierId % 10, greedy sub-row stacking for concurrent bars in one kennel) plus the
// `TraceOverlay` markings (BRON red outline, SAME_KENNEL red, BUUR amber, others dimmed). Built with
// CSS grid/flex + absolutely-positioned bars — NO chart library. Reads via Refine `useCustom` against
// RELATIVE urls (the dataProvider prepends `/api`):
//   GET kennelplanning/timeline?from=&to=&species=         → { groepen, bars }
//   GET kennelplanning/contact-trace?dierId=               → { sourceDierId, sourceKennelIds,
//       neighbourKennelIds, sourceGroepIds, contacten }
// The date-bar (Datum/Periode toggle) lives in Kennelplanning.tsx and feeds the [from,to] window down.

type SpeciesFilter = "HONDEN" | "KATTEN" | "OVERIG";

/** The translate signature (positional %s/%d via `{ args: [...] }`). */
type Translate = (key: string, options?: { args?: unknown[] }, defaultMessage?: string) => string;

// ── Wire contract types (NFR7: Integer ids; number dierId; LocalDate as ISO strings) ───────────────

interface TimelineKennel {
  kennelAsielId?: number | null;
  kennelNummer?: string | null;
}

interface TimelineGroup {
  kennelGroepAsielId?: number | null;
  naam?: string | null;
  ruimteTypeCode?: string | null;
  ruimteTypeEnumId?: string | null;
  kennels?: TimelineKennel[] | null;
}

export interface TimelineBar {
  kennelAsielId?: number | null;
  dierId?: number | null;
  dierNaam?: string | null;
  vanDatum?: string | null;
  totDatum?: string | null;
}

interface TimelineResponse {
  groepen?: TimelineGroup[] | null;
  bars?: TimelineBar[] | null;
}

interface TraceContact {
  dierId?: number | null;
  dierNaam?: string | null;
  contactType?: "SAME_KENNEL" | "NEIGHBOUR" | null;
  daysOverlap?: number | null;
  kennelAsielId?: number | null;
  kennelGroepAsielId?: number | null;
}

interface ContactTraceResponse {
  sourceDierId?: number | null;
  sourceKennelIds?: number[] | null;
  neighbourKennelIds?: number[] | null;
  sourceGroepIds?: number[] | null;
  contacten?: TraceContact[] | null;
}

// ── Geometry constants — ported verbatim from the Jmix TimelineCanvas ──────────────────────────────

const MIN_COL_PX = 14;
const MAX_COL_PX = 32;
const LEFT_GUTTER_PX = 110;
const ROW_HEIGHT_PX = 32;

/** Stable pastel colour per dier (deterministic from dier_id) — the Jmix 10-colour palette. */
const PASTEL_COLOURS = [
  "#cfe7d6", "#fae0a8", "#f3d3e3", "#cfe2f3", "#e0d4ee",
  "#fad6c5", "#d6ebe0", "#f5e0b9", "#dbecf3", "#e8e0d2",
] as const;

export function pastelFor(dierId: number | null | undefined): string {
  if (dierId == null) return PASTEL_COLOURS[0];
  const idx = Math.abs(Math.trunc(dierId)) % PASTEL_COLOURS.length;
  return PASTEL_COLOURS[idx];
}

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  return Math.min(value, max);
}

// ── Date helpers (ISO date strings, no time component) ─────────────────────────────────────────────

/** Parse an ISO date (YYYY-MM-DD or full ISO) to a midday UTC Date (avoids DST/timezone drift). */
export function parseISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0));
}

/** Whole-day difference b − a (both midday-UTC). */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 86_400_000);
}

function isWeekend(d: Date): boolean {
  const dow = d.getUTCDay();
  return dow === 0 || dow === 6;
}

function sameDay(a: Date, b: Date | null): boolean {
  return b != null && a.getTime() === b.getTime();
}

const MONTHS_SHORT_NL = [
  "jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec",
];

// ── Sub-row stacking — greedy assignment ported from the Jmix TimelineCanvas ────────────────────────
// Concurrent bars in one kennel get distinct sub-rows so they don't overlap visually (multi-occupancy).
// A sub-row is only free when its previous bar ended STRICTLY before the new bar starts (bars sharing a
// start day count as overlapping). Returns the sub-row index per bar + the sub-row count per kennel.

interface StackedBar {
  bar: TimelineBar;
  subRowIdx: number;
  /** Stable key for React (idx within its kennel group). */
  key: string;
}

export function stackBars(bars: TimelineBar[]): {
  byKennel: Map<number, StackedBar[]>;
  subRowCount: Map<number, number>;
} {
  const byKennelRaw = new Map<number, TimelineBar[]>();
  for (const bar of bars) {
    if (bar.kennelAsielId == null) continue;
    const list = byKennelRaw.get(bar.kennelAsielId) ?? [];
    list.push(bar);
    byKennelRaw.set(bar.kennelAsielId, list);
  }

  const byKennel = new Map<number, StackedBar[]>();
  const subRowCount = new Map<number, number>();

  for (const [kennelId, list] of byKennelRaw) {
    const sorted = list
      .slice()
      .sort((a, b) => (a.vanDatum ?? "").localeCompare(b.vanDatum ?? ""));
    // subRowEnd[i] = exclusive end date (Date) of the last bar placed in sub-row i.
    const subRowEnd: Date[] = [];
    const stacked: StackedBar[] = [];
    sorted.forEach((bar, i) => {
      const pStart = parseISODate(bar.vanDatum);
      const pEnd = parseISODate(bar.totDatum) ?? new Date(Date.UTC(9999, 0, 1, 12));
      let chosen = -1;
      if (pStart != null) {
        for (let r = 0; r < subRowEnd.length; r++) {
          if (subRowEnd[r].getTime() < pStart.getTime()) {
            chosen = r;
            break;
          }
        }
      }
      if (chosen === -1) {
        chosen = subRowEnd.length;
        subRowEnd.push(pEnd);
      } else {
        subRowEnd[chosen] = pEnd;
      }
      stacked.push({ bar, subRowIdx: chosen, key: `${kennelId}-${i}` });
    });
    byKennel.set(kennelId, stacked);
    subRowCount.set(kennelId, Math.max(1, subRowEnd.length));
  }
  return { byKennel, subRowCount };
}

// ── Per-bar trace classification (ported from TimelineCanvas.buildBar) ──────────────────────────────

type TraceClass = "source" | "same" | "neighbour" | "dim" | null;

function classifyBar(
  bar: TimelineBar,
  trace: ContactTraceResponse | null,
  sourceKennelIds: Set<number>,
  neighbourKennelIds: Set<number>,
  contactDierIds: Set<number>,
): TraceClass {
  if (!trace) return null;
  const dierId = bar.dierId ?? null;
  if (dierId == null) return "dim";
  if (dierId === trace.sourceDierId) return "source";
  if (contactDierIds.has(dierId)) {
    const kennelId = bar.kennelAsielId ?? null;
    if (kennelId != null && sourceKennelIds.has(kennelId)) return "same";
    if (kennelId != null && neighbourKennelIds.has(kennelId)) return "neighbour";
    // Contact dier, but this specific bar sits outside source/neighbour kennels — dim it.
    return "dim";
  }
  return "dim";
}

// ── Component ───────────────────────────────────────────────────────────────────────────────────────

export interface KennelTimelineProps {
  species: SpeciesFilter;
  /** ISO from-date (inclusive). */
  from: string;
  /** ISO to-date (inclusive). */
  to: string;
  /** Day to highlight as "today"/selected (ISO). Defaults to today when omitted. */
  highlightDate?: string | null;
  /** Whether the module is enabled (gates the fetch). */
  enabled: boolean;
  /** The active trace dier (null → no trace). */
  traceDierId: number | null;
  /** Click a bar → start a trace for that dier. */
  onBarClick: (dierId: number, dierNaam: string | null) => void;
  /** Clear the active trace (the banner X). */
  onCloseTrace: () => void;
  /** Label shown in the trace banner (the source dier's name). */
  traceLabel?: string | null;
  t: Translate;
}

export function KennelTimeline({
  species,
  from,
  to,
  highlightDate,
  enabled,
  traceDierId,
  onBarClick,
  onCloseTrace,
  traceLabel,
  t,
}: KennelTimelineProps) {
  // Measure the available width so the column width can be clamped to fit the viewport (like the Jmix
  // cachedTimelineWidthPx). We store the measured width in state (not a ref) so the first real
  // measurement triggers a re-render and feeds the colWidthPx memo; the grid falls back to a sensible
  // default before the first measure. A ResizeObserver keeps it in sync as the container resizes.
  const [measuredWidth, setMeasuredWidth] = useState<number>(1400);
  const [measureEl, setMeasureEl] = useState<HTMLDivElement | null>(null);
  const measureRef = (el: HTMLDivElement | null) => {
    setMeasureEl(el);
    if (el && el.offsetWidth > 0) setMeasuredWidth(el.offsetWidth);
  };
  useEffect(() => {
    if (!measureEl) return;
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (measureEl.offsetWidth > 0) setMeasuredWidth(measureEl.offsetWidth);
    });
    ro.observe(measureEl);
    return () => ro.disconnect();
  }, [measureEl]);

  const timelineUrl = `kennelplanning/timeline?from=${from}&to=${to}&species=${species}`;
  const { data: timelineData, isLoading, isError } = useCustom<TimelineResponse>({
    url: timelineUrl,
    method: "get",
    queryOptions: { enabled, retry: false },
  });

  const traceUrl = `kennelplanning/contact-trace?dierId=${traceDierId ?? ""}`;
  const { data: traceData } = useCustom<ContactTraceResponse>({
    url: traceUrl,
    method: "get",
    queryOptions: { enabled: enabled && traceDierId != null, retry: false },
  });

  const groepen = timelineData?.data?.groepen ?? [];
  const bars = useMemo(() => timelineData?.data?.bars ?? [], [timelineData]);
  const trace = traceDierId != null ? traceData?.data ?? null : null;

  const fromDate = useMemo(() => parseISODate(from), [from]);
  const toDate = useMemo(() => parseISODate(to), [to]);
  const hlDate = useMemo(
    () => parseISODate(highlightDate ?? todayIso()),
    [highlightDate],
  );

  const days = useMemo(() => {
    if (!fromDate || !toDate) return 0;
    return daysBetween(fromDate, toDate) + 1;
  }, [fromDate, toDate]);

  const colWidthPx = useMemo(
    () => clamp(Math.floor((measuredWidth - LEFT_GUTTER_PX) / Math.max(1, days)), MIN_COL_PX, MAX_COL_PX),
    [days, measuredWidth],
  );

  const { byKennel, subRowCount } = useMemo(() => stackBars(bars), [bars]);

  // Trace lookup sets.
  const sourceKennelIds = useMemo(() => new Set(trace?.sourceKennelIds ?? []), [trace]);
  const neighbourKennelIds = useMemo(() => new Set(trace?.neighbourKennelIds ?? []), [trace]);
  const contactDierIds = useMemo(() => {
    const set = new Set<number>();
    for (const c of trace?.contacten ?? []) if (c.dierId != null) set.add(c.dierId);
    return set;
  }, [trace]);

  // Trace banner summary (mirrors the Jmix banner: sameKennel N · neighbour N · totalOverlap days).
  const traceSummary = useMemo(() => {
    const contacten = trace?.contacten ?? [];
    let same = 0;
    let neighbour = 0;
    let overlap = 0;
    for (const c of contacten) {
      if (c.contactType === "SAME_KENNEL") same++;
      else neighbour++;
      overlap += c.daysOverlap ?? 0;
    }
    return { same, neighbour, overlap, count: contacten.length };
  }, [trace]);

  // ── Render ────────────────────────────────────────────────────────────────────────────────────────

  if (!fromDate || !toDate || days <= 0) {
    return <div className="fawkes-kennelplanning__empty">{t("kennelplanning.timeline.invalidRange")}</div>;
  }
  if (isLoading) {
    return <div className="fawkes-kennelplanning__empty">{t("common.loading")}</div>;
  }
  if (isError) {
    return <div className="fawkes-kennelplanning__empty">{t("kennelplanning.loadFailed")}</div>;
  }
  if (groepen.length === 0) {
    return <div className="fawkes-kennelplanning__empty">{t("kennelplanning.empty.noGroups")}</div>;
  }

  // Day index list — used for header + per-row background cells.
  const dayIndexes = Array.from({ length: days }, (_, i) => i);

  // Month header segments (consecutive days grouped by calendar month).
  const monthSegments: Array<{ label: string; widthDays: number }> = [];
  {
    let cursor = fromDate;
    while (cursor.getTime() <= toDate.getTime()) {
      const monthStart = cursor;
      const lastOfMonth = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0, 12));
      const monthEnd = lastOfMonth.getTime() > toDate.getTime() ? toDate : lastOfMonth;
      const monthDays = daysBetween(monthStart, monthEnd) + 1;
      monthSegments.push({
        label: `${MONTHS_SHORT_NL[monthStart.getUTCMonth()]} ${monthStart.getUTCFullYear()}`,
        widthDays: monthDays,
      });
      cursor = addDays(monthEnd, 1);
    }
  }

  const totalGridWidth = LEFT_GUTTER_PX + days * colWidthPx;

  return (
    <div className="fawkes-kennelplanning__timeline" ref={measureRef}>
      <div className="fawkes-kennelplanning__timeline-scroll">
        <div className="fawkes-kennelplanning__timeline-inner" style={{ width: `${totalGridWidth}px` }}>
          {/* ── Sticky header: month row + day row ── */}
          <div className="fawkes-kennelplanning__timeline-header">
            <div className="fawkes-kennelplanning__timeline-month-row">
              <span
                className="fawkes-kennelplanning__timeline-gutter-cell"
                style={{ width: `${LEFT_GUTTER_PX}px` }}
              />
              {monthSegments.map((seg, i) => (
                <div
                  key={`${seg.label}-${i}`}
                  className="fawkes-kennelplanning__timeline-month-cell"
                  style={{ width: `${seg.widthDays * colWidthPx}px` }}
                >
                  {seg.label}
                </div>
              ))}
            </div>
            <div className="fawkes-kennelplanning__timeline-day-row">
              <span
                className="fawkes-kennelplanning__timeline-gutter-cell"
                style={{ width: `${LEFT_GUTTER_PX}px` }}
              />
              {dayIndexes.map((i) => {
                const d = addDays(fromDate, i);
                const cls = [
                  "fawkes-kennelplanning__timeline-day-cell",
                  isWeekend(d) ? "fawkes-kennelplanning__timeline-day-cell--weekend" : "",
                  d.getUTCDate() === 1 ? "fawkes-kennelplanning__timeline-day-cell--month-start" : "",
                  sameDay(d, hlDate) ? "fawkes-kennelplanning__timeline-day-cell--today" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <div key={i} className={cls} style={{ width: `${colWidthPx}px` }}>
                    {d.getUTCDate()}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Body: per-group separator + per-kennel rows with absolutely-positioned bars ── */}
          <div
            className={
              "fawkes-kennelplanning__timeline-body" +
              (trace ? " fawkes-kennelplanning__timeline-body--trace" : "")
            }
          >
            {bars.length === 0 && (
              <div className="fawkes-kennelplanning__empty">{t("kennelplanning.timeline.empty")}</div>
            )}
            {groepen.map((groep, gi) => {
              const kennels = (groep.kennels ?? [])
                .slice()
                .sort((a, b) => safeKennelOrder(a.kennelNummer) - safeKennelOrder(b.kennelNummer));
              return (
                <div key={groep.kennelGroepAsielId ?? gi} className="fawkes-kennelplanning__timeline-group">
                  <div className="fawkes-kennelplanning__timeline-row-separator">
                    <span
                      className="fawkes-kennelplanning__timeline-row-separator-gutter"
                      style={{ width: `${LEFT_GUTTER_PX}px` }}
                    >
                      {groep.naam ?? ""}
                    </span>
                    <div className="fawkes-kennelplanning__timeline-row-separator-fill" />
                  </div>

                  {kennels.map((kennel, ki) => {
                    const kennelId = kennel.kennelAsielId ?? null;
                    const subRows = kennelId != null ? subRowCount.get(kennelId) ?? 1 : 1;
                    const rowHeight = subRows * ROW_HEIGHT_PX;
                    const stacked = kennelId != null ? byKennel.get(kennelId) ?? [] : [];
                    const rowCls = [
                      "fawkes-kennelplanning__timeline-row",
                      kennelId != null && sourceKennelIds.has(kennelId)
                        ? "fawkes-kennelplanning__timeline-row--trace-source"
                        : "",
                      kennelId != null && neighbourKennelIds.has(kennelId)
                        ? "fawkes-kennelplanning__timeline-row--trace-neighbour"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      <div
                        key={kennelId ?? `k-${ki}`}
                        className={rowCls}
                        style={{ height: `${rowHeight}px` }}
                      >
                        <span
                          className="fawkes-kennelplanning__timeline-row-gutter"
                          style={{ width: `${LEFT_GUTTER_PX}px`, lineHeight: `${rowHeight}px` }}
                        >
                          {kennel.kennelNummer ?? ""}
                        </span>
                        {/* Day-background grid (weekend/today tint) */}
                        <div className="fawkes-kennelplanning__timeline-row-days">
                          {dayIndexes.map((i) => {
                            const d = addDays(fromDate, i);
                            const cls = [
                              "fawkes-kennelplanning__timeline-day-bg",
                              isWeekend(d) ? "fawkes-kennelplanning__timeline-day-bg--weekend" : "",
                              sameDay(d, hlDate) ? "fawkes-kennelplanning__timeline-day-bg--today" : "",
                            ]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <div
                                key={i}
                                className={cls}
                                style={{ width: `${colWidthPx}px`, height: `${rowHeight}px` }}
                              />
                            );
                          })}
                        </div>
                        {/* Bars — absolutely positioned within the position:relative row. */}
                        {stacked.map((sb) => (
                          <Bar
                            key={sb.key}
                            stacked={sb}
                            fromDate={fromDate}
                            toDate={toDate}
                            colWidthPx={colWidthPx}
                            traceClass={classifyBar(
                              sb.bar,
                              trace,
                              sourceKennelIds,
                              neighbourKennelIds,
                              contactDierIds,
                            )}
                            onBarClick={onBarClick}
                            t={t}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Sticky trace banner (count of contacts + overlap summary) with an X to clear ── */}
      {trace && (
        <div className="fawkes-kennelplanning__trace-banner" role="status">
          <span className="fawkes-kennelplanning__trace-banner-label">
            {t("kennelplanning.trace.banner.prefix")} {traceLabel ?? ""}
          </span>
          <span className="fawkes-kennelplanning__trace-banner-counts">
            {traceSummary.count === 0
              ? t("kennelplanning.trace.banner.noContacts")
              : `${t("kennelplanning.trace.banner.sameKennel")} ${traceSummary.same} · ` +
                `${t("kennelplanning.trace.banner.neighbour")} ${traceSummary.neighbour} · ` +
                `${traceSummary.overlap} ${t("kennelplanning.trace.banner.overlapDays")}`}
          </span>
          <button
            type="button"
            className="fawkes-kennelplanning__trace-banner-close"
            aria-label={t("kennelplanning.trace.banner.close")}
            onClick={onCloseTrace}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// ── A single bar (clipped to [from,to], pastel-per-dier, sub-row offset, trace-classified) ──────────

function Bar({
  stacked,
  fromDate,
  toDate,
  colWidthPx,
  traceClass,
  onBarClick,
  t,
}: {
  stacked: StackedBar;
  fromDate: Date;
  toDate: Date;
  colWidthPx: number;
  traceClass: TraceClass;
  onBarClick: (dierId: number, dierNaam: string | null) => void;
  t: Translate;
}) {
  const { bar, subRowIdx } = stacked;
  const barFrom = parseISODate(bar.vanDatum);
  // Open period → up to "today", parsed through the SAME midday-UTC basis as every other date so the
  // daysBetween/round math stays consistent (a raw `new Date()` carries a local time-of-day component
  // that can shift the rounded day difference by ±1).
  const barTo =
    parseISODate(bar.totDatum) ?? parseISODate(todayIso())!;
  if (!barFrom) return null;
  // Clip to the visible window; skip bars entirely outside it.
  if (barFrom.getTime() > toDate.getTime() || barTo.getTime() < fromDate.getTime()) return null;
  const clipFrom = barFrom.getTime() < fromDate.getTime() ? fromDate : barFrom;
  const clipTo = barTo.getTime() > toDate.getTime() ? toDate : barTo;
  const offsetDays = daysBetween(fromDate, clipFrom);
  const widthDays = daysBetween(clipFrom, clipTo) + 1;

  const top = 4 + subRowIdx * ROW_HEIGHT_PX;
  const colour = pastelFor(bar.dierId);
  const name = bar.dierNaam ?? "";

  const cls = [
    "fawkes-kennelplanning__timeline-bar",
    traceClass === "source" ? "fawkes-kennelplanning__timeline-bar--trace-source" : "",
    traceClass === "same" ? "fawkes-kennelplanning__timeline-bar--trace-contact-same" : "",
    traceClass === "neighbour" ? "fawkes-kennelplanning__timeline-bar--trace-contact-neighbour" : "",
    traceClass === "dim" ? "fawkes-kennelplanning__timeline-bar--trace-dim" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const clickable = bar.dierId != null;

  return (
    <div
      className={cls}
      style={{
        top: `${top}px`,
        left: `${LEFT_GUTTER_PX + offsetDays * colWidthPx}px`,
        width: `${widthDays * colWidthPx - 2}px`,
        height: `${ROW_HEIGHT_PX - 8}px`,
        background: colour,
      }}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={name}
      onClick={clickable ? () => onBarClick(bar.dierId!, bar.dierNaam ?? null) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onBarClick(bar.dierId!, bar.dierNaam ?? null);
              }
            }
          : undefined
      }
    >
      {traceClass === "source" && (
        <span className="fawkes-kennelplanning__timeline-bar-tag">
          {t("kennelplanning.trace.bar.source")}
        </span>
      )}
      <span className="fawkes-kennelplanning__timeline-bar-name">{name}</span>
    </div>
  );
}

/** Numeric kennel-number ordering with a stable non-numeric fallback (ported from Jmix). */
function safeKennelOrder(value: string | null | undefined): number {
  if (!value || !value.trim()) return Number.MAX_SAFE_INTEGER;
  const n = Number.parseInt(value.trim(), 10);
  if (!Number.isNaN(n)) return n;
  return Number.MAX_SAFE_INTEGER - 1;
}
